import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDateTime, fmtDate } from "@/lib/cc";
import { FileText, Shield, ShieldCheck, Users, ArrowLeft, ExternalLink, Phone, Mail, Briefcase, CheckCircle2, AlertCircle, Clock } from "lucide-react";

const STATUS_PILL = {
  verified: { color: "pill-success", icon: CheckCircle2, label: "Clear" },
  rejected: { color: "pill-danger", icon: AlertCircle, label: "Not Clear" },
  pending: { color: "pill-warning", icon: Clock, label: "Pending review" },
  cannot_verify: { color: "pill-danger", icon: AlertCircle, label: "Cannot verify" },
  expired: { color: "pill-danger", icon: AlertCircle, label: "Expired" },
  missing: { color: "pill-neutral", icon: AlertCircle, label: "Not uploaded" },
  needs_more_info: { color: "pill-warning", icon: AlertCircle, label: "Needs info" },
  not_eligible: { color: "pill-danger", icon: AlertCircle, label: "Not eligible" },
};

export default function InterviewPackPage() {
  const { cid, iid } = useParams();
  const nav = useNavigate();
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/candidates/${cid}/interviews/${iid}/pack`)
      .then(({ data }) => setPack(data))
      .catch(() => toast.error("Could not load interview pack"))
      .finally(() => setLoading(false));
  }, [cid, iid]);

  if (loading) return <div className="cc-card p-10 text-center text-[var(--cc-text-soft)]">Loading interview pack…</div>;
  if (!pack) return <div className="cc-card p-10 text-center">Not found</div>;

  const { candidate, job, interview, template, checks } = pack;
  const StatusBadge = ({ status }) => {
    const meta = STATUS_PILL[status] || STATUS_PILL.missing;
    return <span className={`pill ${meta.color} inline-flex items-center gap-1`}><meta.icon className="w-3 h-3" /> {meta.label}</span>;
  };

  return (
    <div className="fade-up" data-testid="interview-pack">
      <button className="btn-ghost mb-4" onClick={() => nav(-1)}><ArrowLeft className="w-4 h-4" /> Back</button>

      <div className="cc-card p-5 mb-5 flex flex-wrap gap-4 items-center justify-between">
        <div>
          <div className="label-eyebrow mb-1">Interview Pack · 30-second prep</div>
          <h1 className="font-display text-3xl font-bold">{candidate.first_name} {candidate.last_name}</h1>
          <div className="text-sm text-[var(--cc-text-soft)] mt-1 flex flex-wrap gap-4">
            <span className="inline-flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {candidate.job_title}</span>
            <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {candidate.email}</span>
            {candidate.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {candidate.phone}</span>}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs label-eyebrow">Interview</div>
          <div className="capitalize font-semibold">{interview.type?.replace("_", " ")}</div>
          <div className="text-xs text-[var(--cc-text-soft)]">{fmtDateTime(interview.scheduled_at)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Left: candidate & checks */}
        <div className="lg:col-span-5 space-y-5">
          <div className="cc-card p-5">
            <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-3"><FileText className="w-4 h-4 text-[var(--cc-primary)]" /> CV / Documents</h2>
            {candidate.cv_file?.file_id ? (
              <button
                type="button"
                className="btn-secondary"
                data-testid="pack-cv-link"
                onClick={async () => {
                  try {
                    const { data } = await api.get(`/admin/cv-presigned-url/${candidate.cv_file.file_id}`);
                    window.open(data.url, "_blank", "noopener");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Could not open CV");
                  }
                }}
              >
                <ExternalLink className="w-4 h-4" /> Open CV
              </button>
            ) : <div className="text-sm text-[var(--cc-text-soft)]">No CV uploaded.</div>}
            {candidate.experience_years && <div className="text-sm mt-3"><strong>Experience:</strong> {candidate.experience_years} yr</div>}
          </div>

          <div className="cc-card p-5">
            <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-3"><ShieldCheck className="w-4 h-4 text-[var(--cc-primary)]" /> Pre-employment checks</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between" data-testid="pack-check-dbs">
                <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-[var(--cc-text-soft)]" /> <span className="font-medium">DBS</span></div>
                <StatusBadge status={checks.dbs.status} />
              </div>
              <div className="flex items-center justify-between" data-testid="pack-check-rtw">
                <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[var(--cc-text-soft)]" /> <span className="font-medium">Right to Work</span></div>
                <StatusBadge status={checks.rtw.status} />
              </div>
              <div className="flex items-center justify-between" data-testid="pack-check-refs">
                <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[var(--cc-text-soft)]" /> <span className="font-medium">References</span></div>
                <span className={`pill ${checks.references.received === checks.references.total && checks.references.total > 0 ? "pill-success" : "pill-warning"}`}>
                  {checks.references.received}/{checks.references.total || 0} received
                </span>
              </div>
            </div>
          </div>

          {job && (
            <div className="cc-card p-5">
              <h2 className="font-display font-bold text-lg mb-3">About the role</h2>
              <div className="text-sm">
                <div><strong>{job.title}</strong> · {job.location}</div>
                {job.salary && <div className="text-[var(--cc-text-soft)] mt-1">Salary: {job.salary}</div>}
                {job.contract_type && <div className="text-[var(--cc-text-soft)]">Contract: {job.contract_type}</div>}
                {job.hours_per_week && <div className="text-[var(--cc-text-soft)]">Hours/week: {job.hours_per_week}</div>}
              </div>
            </div>
          )}
        </div>

        {/* Right: template questions */}
        <div className="lg:col-span-7 space-y-5">
          <div className="cc-card p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold text-lg">Scoring rubric (1–5)</h2>
              {template ? <span className="pill pill-info">{template.name}</span> : <span className="pill pill-neutral">Default rubric</span>}
            </div>
            {template?.competencies?.length > 0 && (
              <div className="text-xs text-[var(--cc-text-soft)] mb-3">Competencies: {template.competencies.join(" · ")}</div>
            )}
            <ol className="space-y-3 list-decimal pl-5">
              {(template?.questions?.length ? template.questions.map((q, i) => q.text || q.competency || `Question ${i + 1}`) :
                ["Person-centred care", "Safeguarding", "CQC awareness", "Teamwork"]).map((q, i) => (
                <li key={i} className="border-l-2 border-[var(--cc-primary)]/30 pl-3 py-1" data-testid={`pack-q-${i}`}>
                  <div className="text-sm font-medium">{q}</div>
                  <div className="text-[11px] text-[var(--cc-text-soft)] mt-1">1 = Below expectations · 3 = Meets · 5 = Exceeds</div>
                </li>
              ))}
            </ol>
          </div>

          {interview.instructions && (
            <div className="cc-card p-5">
              <h2 className="font-display font-bold text-lg mb-2">Instructions for candidate</h2>
              <p className="text-sm">{interview.instructions}</p>
            </div>
          )}

          {interview.panel?.length > 0 && (
            <div className="cc-card p-5">
              <h2 className="font-display font-bold text-lg mb-2">Panel</h2>
              <div className="flex flex-wrap gap-2">
                {interview.panel.map((p) => <span key={p} className="pill pill-neutral">{p}</span>)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
